import { dropRight, isEmpty, last, map } from 'lodash';
import probe from 'probe-image-size';

// import sizeOf from './image-size-promise-wrapper';

const MRE_EXTENSION = 'mre';
const TFW_EXTENSION = 'wld';

/**
 * Checks the  file for the image.
 *
 * @param {number[]} data - The parameters of the transformation.
 * @param {number} imageHeight - The image height.
 * @param {number} imageWidth - The image width.
 */
export const getCornersFromData = (data, imageHeight, imageWidth) => {
  const [A, D, B, E, C, F] = data;

  const topLeftLong = C;
  const topLeftLat = F;

  const topRightLong = A * imageWidth + C;
  const topRightLat = D * imageWidth + F;

  const bottomLeftLong = B * imageHeight + C;
  const bottomLeftLat = E * imageHeight + F;

  const topLeft = { lat: topLeftLat, lng: topLeftLong };
  const topRight = { lat: topRightLat, lng: topRightLong };
  const bottomLeft = { lat: bottomLeftLat, lng: bottomLeftLong };

  return { topLeft, topRight, bottomLeft };
};

/**
 * Checks the MRE file for the image.
 *
 * @async
 * @param {string} imageURL - The url of the image to check.
 * @returns {object} - The coordinates for the corners of the image, or empty object if not existing.
 */
export const checkMRE = async imageURL => {
  try {
    const imageExtension = last(imageURL.split('.'));
    const filePath = imageURL.replace(imageExtension, MRE_EXTENSION);
    const response = await fetch(filePath, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const data = await response.json();

    const topLeft = { lat: data[0][1], lng: data[0][0] };
    const topRight = { lat: data[1][1], lng: data[1][0] };
    const bottomLeft = { lat: data[2][1], lng: data[2][0] };

    return { topLeft, topRight, bottomLeft };
  } catch (error) {
    return {};
  }
};

/**
 * Checks the TFW file for the image.
 *
 * @async
 * @param {string} imageURL - The url of the image to check.
 * @returns {object} - The coordinates for the corners of the image, or empty object if not existing.
 */
export const checkTFW = async imageURL => {
  try {
    const imageExtension = last(imageURL.split('.'));
    const filePath = imageURL.replace(imageExtension, TFW_EXTENSION);
    const response = await fetch(filePath, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const dataAsText = await response.text();
    const data = map(dropRight(dataAsText.split('\n')), value => Number(value));

    const { width, height } = await probe(imageURL);
    const { topLeft, topRight, bottomLeft } = getCornersFromData(data, height, width);

    return { topLeft, topRight, bottomLeft };
  } catch (error) {
    return {};
  }
};

/**
 * Checks the georeferenced complete world format file for the image.
 *
 * @async
 * @param {string} imageURL - The url of the image to check.
 * @returns {object} - The coordinates for the corners of the image, or empty object if not existing.
 */
export const checkTotalExtW = async imageURL => {
  try {
    const filePath = `${imageURL}w`;
    const response = await fetch(filePath, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const dataAsText = await response.text();
    const data = map(dropRight(dataAsText.split('\n')), value => Number(value));

    const { width, height } = await probe(imageURL);
    const { topLeft, topRight, bottomLeft } = getCornersFromData(data, height, width);

    return { topLeft, topRight, bottomLeft };
  } catch (error) {
    return {};
  }
};

/**
 * Checks the georeferenced world format file for the image.
 *
 * @async
 * @param {string} imageURL - The url of the image to check.
 * @returns {object} - The coordinates for the corners of the image, or empty object if not existing.
 */
export const checkParcialExtW = async imageURL => {
  try {
    const imageExtension = last(imageURL.split('.'));
    const splitExtension = imageExtension.split('');
    const filePath = imageURL.replace(
      imageExtension,
      `${splitExtension[0]}${last(splitExtension)}w`
    );
    const response = await fetch(filePath, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const dataAsText = await response.text();
    const data = map(dropRight(dataAsText.split('\n')), value => Number(value));

    const { width, height } = await probe(imageURL);
    const { topLeft, topRight, bottomLeft } = getCornersFromData(data, height, width);

    return { topLeft, topRight, bottomLeft };
  } catch (error) {
    return {};
  }
};

/**
 * Checks if an image is georeferenced and gets its georeferencing if exists.
 *
 * @async
 * @param {string} imageURL - The url of the image to check.
 * @returns {object} The georeferenced corners of the image.
 */
const getImageGeoreferencing = async imageURL => {
  try {
    let data;

    data = await checkMRE(imageURL);
    if (!isEmpty(data)) return data;

    data = await checkTFW(imageURL);
    if (!isEmpty(data)) return data;

    data = await checkTotalExtW(imageURL);
    if (!isEmpty(data)) return data;

    data = await checkParcialExtW(imageURL);
    if (!isEmpty(data)) return data;

    return { topLeft: {}, topRight: {}, bottomLeft: {} };
  } catch (error) {
    throw new Error(error);
  }
};

export default getImageGeoreferencing;
